<template>
  <div
    v-if="!isReaction"
    :class="`row attendance-handle-message items-center full-width ${alignMsg} ${isReverse ? 'reverse' : ''} ${isHighlighted ? 'highlight-message' : ''}`"
    style="margin-top: 2px"
    v-observe-visibility="msg.readedAt? false: handleVisibility"
  >
    <div :class="['col-auto box-text q-px-sm q-pt-sm dont-break-out max-width-350', textBorder, textAlign, textColor, { 'has-reaction': msg.reaction }]">
      <AttendanceChatQuotedMessage
        v-if="msg.parentMessage"
        :contact-name="contactName"
        :message="msg.parentMessage"
        @scroll-to-parent="$emit('scroll-to-parent', msg.parentRef)"
      />
      <div v-if="isAudio">
        <AudioPlayer
          :url="fileUrl"
          :name="fileName"
          :mime="fileMime"
        />
      </div>

      <div v-if="isVideo || (isTemplate && templateVideo)">
        <VideoPlayer
          :url="fileUrl"
          :name="fileName"
          :mime="fileMime"
        />
      </div>

      <div v-if="isImage || (isTemplate && templateImg)">
        <ImgView
          :msg="msg"
          :url="fileUrl"
          :name="fileName"
          :reverse="isReverse"
        />
      </div>

      <div v-if="isContact">
        <ContactView
          v-for="(contact, index) in attach"
          :key="index"
          :contacts="contact"
        />
      </div>

      <div v-if="isDoc || (isTemplate && templatePdf)">
        <DocView
          :url="fileUrl"
          :name="fileName"
          :direction="msg.direction"
        />
      </div>

      <div v-if="isLocalization">
        <LocationView
          :latitude="attach.latitude"
          :longitude="attach.longitude"
          :name="attach.name"
          :address="attach.address"
          :url="attach.url"
        />
      </div>

      <div
        class="q-pt-sm"
        v-if="message && (isVideo || isAudio || isTemplate || isDoc || isContact || isImage || isLocalization)"
      />
      <div
        v-if="message && !isLink"
        :class="['max-width-350 text-left', textColor]"
      >
        <span v-html="formatMessageText(message)" />
      </div>
      <a
        v-if="message && isLink"
        @click.prevent="openLinkInBrowser"
        :class="['max-width-350', textColor, 'message-link']"
      >{{ message }}</a>

      <div
        class="status-and-reaction-container row"
        :class="isReverse ? 'justify-end' : 'justify-start'"
      >
        <AttendanceChatStatus
          :status="msg.status"
          :status-icon="msg.statusIcon"
          :is-reverse="isReverse"
          :created-at="msg.createdAt"
          :delivered-at="msg.deliveredAt"
          :readed-at="msg.readedAt"
        />

        <AttendanceChatReaction
          v-if="msg.reaction"
          :reaction="msg.reaction"
          :is-reverse="isReverse"
        />
      </div>
    </div>

    <div class="col-auto msg-options">
      <div class="actions-grid">
        <span
          v-if="isVideo || isDoc || isAudio || isImage"
          class="action-item cursor-pointer"
          @click="openExternal"
        >
          <DownloadIcon />
          <q-tooltip>Fazer download</q-tooltip>
        </span>

        <span
          v-if="message"
          class="action-item cursor-pointer"
        >
          <CopyIcon @click="handleCopyMessage" />
          <q-tooltip>Copiar Mensagem</q-tooltip>
        </span>

        <span
          v-if="isVideo || isDoc || isAudio || isImage"
          class="action-item cursor-pointer"
        >
          <CopyLinkIcon @click="handleCopyLink" />
          <q-tooltip>Copiar Link</q-tooltip>
        </span>

        <span
          v-if="isWhatsApp && isChatActive && !isMessageTooOld"
          class="action-item cursor-pointer"
          @click="handleReplyMessage"
        >
          <q-icon name="mdi-reply" />
          <q-tooltip>Responder</q-tooltip>
        </span>
      </div>
    </div>

    <div class="col" />
  </div>
</template>

<script>
import Vue from 'vue'
import api from '@/libs/api'
import Helpers from '@/libs/helpers'
import { ObserveVisibility } from 'vue-observe-visibility'
import AudioPlayer from './AudioPlayer'
import moment from 'moment-timezone'
import VideoPlayer from './VideoPlayer'
import ImgView from './ImgView'
import DocView from './DocView'
import ContactView from './ContactView'
import LocationView from './LocationView'
import AttendanceChatReaction from './AttendanceChatReaction.vue'
import AttendanceChatStatus from './AttendanceChatStatus'
import AttendanceChatQuotedMessage from './AttendanceChatQuotedMessage.vue'
import DownloadIcon from '@/assets/icons/download.svg'
import CopyIcon from '@/assets/icons/copy.svg'
import CopyLinkIcon from '@/assets/icons/CopyLinkIcon.svg'

const ipcRenderer = window.ipcRenderer
const shell = window.shell
Vue.directive('observe-visibility', ObserveVisibility)

export default {
  components: {
    AttendanceChatReaction,
    AttendanceChatStatus,
    AttendanceChatQuotedMessage,
    AudioPlayer,
    ContactView,
    CopyIcon,
    CopyLinkIcon,
    DocView,
    DownloadIcon,
    ImgView,
    VideoPlayer,
    LocationView
  },

  props: {
    msg: {},
    contactName: { type: String, default: '' },
    isHighlighted: { type: Boolean, default: false },
    isWhatsApp: { type: Boolean, default: false },
    isChatActive: { type: Boolean, default: true }
  },

  data () {
    const handleVisibility = {
      callback: this.visibilityChanged,
      once: true
    }
    return {
      downloadText: 'Fazer Download do Arquivo',
      handleVisibility
    }
  },

  computed: {
    attach () {
      const vm = this
      if (!vm.msg) return {}
      if (!vm.msg.message) return {}

      const isArray = vm.msg.message.startsWith('[') && vm.msg.message.endsWith(']')
      const shouldAttemptParse = ((vm.msg.message.startsWith('{') && vm.msg.message.endsWith('}')) || isArray)

      if (!shouldAttemptParse) return {}
      const strMessage = vm.msg.message.trim().replace(/(\r\n|\n)/g, '\\n')

      try {
        return JSON.parse(strMessage)
      } catch {
        return {}
      }
    },

    fileName () {
      const vm = this
      if (vm.attach?.filename) return vm.attach.filename
      if (vm.attach?.fileName) return vm.attach.fileName
      return ''
    },

    fileMime () {
      const vm = this
      return vm.attach.fileMimeType ?? ''
    },

    fileUrl () {
      const vm = this
      const fileUrl = vm.attach.fileUrl || vm.attach.header
      const isValidUrl = isValidHttpUrl(fileUrl)
      const isPDF = vm.attach?.fileMimeType === 'application/pdf'

      if (isValidUrl) return fileUrl
      if (isPDF) return api.getUrlPDF(fileUrl)

      return api.getAttachmentUrl(vm.msg.id, fileUrl)
    },

    templateImg () {
      const header = this.attach?.header
      return header.startsWith('http') && (/\.(jpg|jpeg|png)$/.test(header)) ? header : ''
    },

    templatePdf () {
      const header = this.attach?.header
      return header.startsWith('http') && (/\.pdf$/.test(header)) ? header : ''
    },

    templateVideo () {
      const header = this.attach?.header
      return header.startsWith('http') && (/\.mp4$/.test(header)) ? header : ''
    },

    message () {
      const vm = this
      if (!vm.msg) return ''
      if (vm.msg.type === 'REACTION') return ''
      if (vm.msg.type === 'CONTACT') return ''

      let json = null
      let message = vm.msg.message.trim()

      if (message.startsWith('{') && message.endsWith('}')) {
        json = parseJSON(message)
        if (!json) return message

        message = ''
        if (json.fileCaption) message = json?.fileCaption
        if (json.body) message = json?.body
        if (json.data) message = json.data
      }

      if (vm.isAudio) return message

      if (!message && vm.fileName) return vm.fileName
      if (message && vm.fileName) return `${vm.fileName}\n${message}`

      return message
    },

    isInvalidMessage () {
      return !this.isText && !this.isAudio && !this.isImage && !this.isDoc && !this.isVideo
    },

    isText () {
      return this.msg?.type === 'TEXT'
    },

    isAudio () {
      return this.msg?.type === 'AUDIO'
    },

    isTemplate () {
      return this.msg?.type === 'TEMPLATE'
    },

    isImage () {
      const vm = this
      if (!vm.msg) return false
      return vm.msg.type === 'IMAGE' || vm.fileMime.includes('image/')
    },

    isDoc () {
      const vm = this
      if (!vm.msg) return false
      return vm.msg.type === 'DOC' && !vm.fileMime.includes('image/')
    },

    isVideo () {
      return this.msg?.type === 'VIDEO'
    },

    isContact () {
      return this.msg?.type === 'CONTACT'
    },

    isLocalization () {
      return this.msg?.type === 'LOCALIZATION'
    },

    isReaction () {
      return this.msg?.type === 'REACTION'
    },

    alignMsg () {
      return this.msg.direction === 'IN' ? 'q-pr-xl justify-start' : 'q-pl-xl justify-end'
    },

    isDirectionOut () {
      return this.msg.direction === 'OUT'
    },

    isDirectionAuto () {
      return this.msg.direction === 'AUTO'
    },

    textBorder () {
      const vm = this
      if (vm.isText || vm.isVideo) return vm.msg.direction === 'IN' ? 'border-in' : 'border-out'
      return vm.msg.direction === 'IN' ? 'item-border-in' : 'item-border-out'
    },

    textAlign () {
      return this.msg.direction === 'IN' ? 'self-start text-left' : 'self-end text-left'
    },

    textColor () {
      return this.msg.direction === 'IN' ? 'bg-white' : 'bg-green-msg'
    },

    isReverse () {
      return (this.msg.direction === 'OUT' || this.msg.direction === 'AUTO')
    },

    isLink () {
      return this.msg?.type === 'TEXT' && isValidHttpUrl(this.msg?.message)
    },

    isMessageTooOld () {
      if (!this.msg.createdAt) return false
      const thirtyDaysAgo = moment().subtract(30, 'days')
      return moment(this.msg.createdAt).isBefore(thirtyDaysAgo)
    }
  },

  methods: {
    formatMessageText (text) {
      if (text && text.includes('\n')) {
        const parts = text.split('\n')
        const firstLine = parts[0]
        const restOfMessage = parts.slice(1).join('\n')

        if (this.fileName && firstLine === this.fileName && restOfMessage.trim()) {
          return firstLine + '\n' + Helpers.formatMarkdownText(restOfMessage)
        }

        return Helpers.formatMarkdownText(text)
      }

      if (this.fileName && text === this.fileName) {
        return text
      }

      return Helpers.formatMarkdownText(text)
    },

    visibilityChanged (isVisible) {
      if (!isVisible) return

      const vm = this
      if (vm.msg.readedAt) return
      vm.$emit('handlevisibility', vm.msg)
    },

    async openExternal () {
      const downloadUrl = await api.getDownloadUrl(this.fileUrl)
      if (!downloadUrl) return

      ipcRenderer.send('download', {
        payload: {
          url: downloadUrl,
          name: this.fileName
        }
      })
    },

    handleCopyLink () {
      Helpers.copy(this, this.fileUrl, 'Link copiado!')
    },

    handleCopyMessage () {
      Helpers.copy(this, this.message, 'Mensagem copiada!')
    },

    openLinkInBrowser () {
      shell.openExternal(this.message)
    },

    handleReplyMessage () {
      this.$emit('reply-to-message', this.msg)
    }
  }
}

const isValidHttpUrl = (string) => {
  let url
  try {
    url = new URL(string)
  } catch {
    return false
  }
  return url.protocol === 'http:' || url.protocol === 'https:'
}

const parseJSON = (str) => {
  try {
    const normalizedStr = str.replace(/(\r\n|\n|\r)/g, '\\n')
    return JSON.parse(normalizedStr)
  } catch (error) {
    console.error('Erro ao fazer parse do JSON:', error, str)
    return null
  }
}

</script>

<style lang="sass">
  .message-link
    color: #0056b3
    text-decoration: underline
    cursor: pointer

  .check-whatsapp
    color: #51bcea

  .attendance-handle-message
    .msg-options
      display: none
      margin-left: 6px

    &.reverse
      .msg-options
        margin-left: 0
        margin-right: 6px

    &:hover
      .msg-options
        display: flex

    audio::-webkit-media-controls-panel
      background-color: #eee

    audio
      height: 15px

    .border-out
      border-radius: 5px 7px 0 5px
      border: 1px solid #ece6e6

    .border-in
      border-radius: 7px 5px 5px 0
      border: 1px solid #ece6e6

    .item-border-out
      border-radius: 5px 7px 0 5px
      border: 1px solid #ece6e6

    .item-border-in
      border-radius: 7px 5px 5px 0
      border: 1px solid #ece6e6

    .box-text
      font-family: Roboto
      font-style: normal
      font-weight: 400
      font-stretch: 100%
      font-size: 14.2px
      line-height: 19px
      font-feature-settings: "kern"
      position: relative
      color: rgb(17, 27, 33)
      position: relative

      &.has-reaction
        margin-bottom: 20px

    .box-time
      font-family: Roboto
      font-style: normal
      font-weight: normal
      font-size: 11px
      line-height: 13px
      color: #667781

    .dont-break-out
      white-space: pre-wrap
      overflow-wrap: break-word
      word-wrap: break-word
      -ms-word-break: break-all
      word-break: break-all
      word-break: break-word
      -ms-hyphens: auto
      -moz-hyphens: auto
      -webkit-hyphens: auto
      hyphens: auto

    .actions-grid
      display: grid
      grid-auto-flow: column
      grid-template-rows: repeat(2, 1fr)
      column-gap: 4px
      row-gap: 0

    .action-item
      text-align: center

    .max-width-350
      max-width: 350px

  .status-and-reaction-container
    display: flex
    align-items: flex-end

  .highlight-message
    animation: highlight 2s ease-out

  @keyframes highlight
    0%
      background-color: rgba(0, 120, 255, 0.2)
    100%
      background-color: transparent

</style>
